import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Divider,
  FormControlLabel, IconButton, Paper, Snackbar, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import {
  getIntakeFormConfig, updateIntakeFormConfig,
  type IntakeFormConfigView,
} from '../../../services/intakeService';

/**
 * Admin → Intake Form. What clients see on the public intake link:
 * which catalog categories/tests are offered, the must-tick checklist, and
 * the instructions blurb. Catalog-driven — new tests appear automatically
 * once their category is visible.
 */
export const IntakeFormSettingsPage = () => {
  const [view, setView] = useState<IntakeFormConfigView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [blurb, setBlurb] = useState('');
  const [newItemText, setNewItemText] = useState('');

  const load = useCallback(async () => {
    try {
      const v = await getIntakeFormConfig();
      setView(v);
      setBlurb(v.config.instructionsBlurb);
    } catch (err) {
      setError((err as Error).message || 'Failed to load the intake form settings.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch: Parameters<typeof updateIntakeFormConfig>[0]) => {
    try {
      const v = await updateIntakeFormConfig(patch);
      setView(v);
      setBlurb(v.config.instructionsBlurb);
      setSaved(true);
    } catch (err) {
      setError((err as Error).message || 'Failed to save.');
      load();
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }
  if (!view) {
    return <Box sx={{ p: 3 }}><Alert severity="error">{error ?? 'Could not load settings.'}</Alert></Box>;
  }

  const { config, availableCategories, tests } = view;
  const hiddenSet = new Set(config.hiddenTestKeys.map((k) => k.toUpperCase()));
  const hiddenTests = tests.filter((t) => hiddenSet.has(t.key.toUpperCase()));

  const toggleCategory = (name: string, visible: boolean) => {
    const next = visible
      ? [...config.visibleCategories, name]
      : config.visibleCategories.filter((c) => c !== name);
    if (next.length === 0) {
      setError('At least one category must stay visible.');
      return;
    }
    save({ visibleCategories: next });
  };

  const addChecklistItem = () => {
    const text = newItemText.trim();
    if (!text) return;
    const id = `custom-${Date.now().toString(36)}`;
    save({ checklistItems: [...config.checklistItems, { id, text }] });
    setNewItemText('');
  };

  return (
    <Box sx={{ p: 3, maxWidth: 860 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Intake Form</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Controls the public client intake link. Tests come straight from the Test Catalogue —
        adding a test to a visible category puts it on the form automatically.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Visible categories */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Offered categories</Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.5 }}>
          {availableCategories.map((cat) => (
            <FormControlLabel
              key={cat.name}
              control={
                <Checkbox
                  checked={config.visibleCategories.includes(cat.name)}
                  onChange={(e) => toggleCategory(cat.name, e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  {cat.name} <Typography component="span" variant="caption" color="text.secondary">({cat.testCount} tests)</Typography>
                </Typography>
              }
            />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Soil / advanced categories stay hidden until those tests go live — tick them here when they do.
        </Typography>
      </Paper>

      {/* Hidden tests within visible categories */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Hidden tests</Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Autocomplete
          multiple
          options={tests}
          getOptionLabel={(t) => (t.astm ? `${t.name} (${t.astm})` : t.name)}
          groupBy={(t) => t.category}
          value={hiddenTests}
          isOptionEqualToValue={(a, b) => a.key.toUpperCase() === b.key.toUpperCase()}
          onChange={(_, value) => save({ hiddenTestKeys: value.map((t) => t.key) })}
          renderTags={(value, getTagProps) =>
            value.map((t, i) => (
              <Chip {...getTagProps({ index: i })} key={t.key} label={t.name} size="small" />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder="Pick tests to hide from clients…"
              helperText="These stay in the catalogue but never appear on the public form." />
          )}
        />
      </Paper>

      {/* Checklist */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Must-tick checklist</Typography>
      <Paper variant="outlined" sx={{ mb: 3 }}>
        {config.checklistItems.map((item, i) => (
          <Box key={item.id}>
            {i > 0 && <Divider />}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>{item.text}</Typography>
              <Tooltip title={config.checklistItems.length <= 1 ? 'At least one item is required' : 'Remove item'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={config.checklistItems.length <= 1}
                    onClick={() => save({ checklistItems: config.checklistItems.filter((c) => c.id !== item.id) })}
                  >
                    <DeleteOutline sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        ))}
        <Divider />
        <Box sx={{ display: 'flex', gap: 1, p: 1.5 }}>
          <TextField
            size="small" fullWidth placeholder="New checklist item…"
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addChecklistItem(); } }}
          />
          <Button size="small" variant="outlined" startIcon={<Add />} onClick={addChecklistItem}
            disabled={!newItemText.trim()} sx={{ textTransform: 'none', flexShrink: 0 }}>
            Add
          </Button>
        </Box>
      </Paper>

      {/* Blurb */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Instructions blurb</Typography>
      <TextField
        value={blurb}
        onChange={(e) => setBlurb(e.target.value)}
        onBlur={() => { if (blurb.trim() && blurb !== config.instructionsBlurb) save({ instructionsBlurb: blurb }); }}
        helperText="Shown above the checklist on the client form. Saves on blur."
        fullWidth multiline rows={3} size="small"
      />

      <Snackbar
        open={saved}
        autoHideDuration={1500}
        onClose={() => setSaved(false)}
        message="Saved"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default IntakeFormSettingsPage;
